import { useMemo, useContext } from 'react';
import { CodeProps } from 'react-markdown/lib/ast-to-react';
import SyntaxHighlighter from 'react-syntax-highlighter';
import {
  atomOneDarkReasonable,
  atomOneLight,
} from 'react-syntax-highlighter/dist/cjs/styles/hljs';
import { useColorTheme } from 'src/hooks';
import { MoveToEditorTool } from 'src/v2/components/ThreadMessage/components/MoveToEditorTool';
import { ArtifactType, ProgrammingLanguage } from 'src/types';
import SessionContext from 'src/contexts/SessionContext';
import { CopyCodeButton } from 'src/v2/components/CopyCodeButton';

interface CodeMarkdownProps extends CodeProps {
  isStreaming?: boolean;
}

export const CodeMarkdown = ({
  node,
  inline,
  className = 'language-code',
  style,
  children,
  isStreaming = true,
  ...props
}: CodeMarkdownProps) => {
  const { isDarkTheme } = useColorTheme();
  const { isVisibleTaskViewModal } = useContext(SessionContext);

  const syntaxHighlighterLanguage = /language-(\w+)/.exec(className || '');

  const sanitizedText = useMemo(
    () =>
      String(children)
        .replace(/\u00A0/g, ' ')
        .replace(/&nbsp;/g, ' ')
        .replace(/\r\n|\n|\r/g, '\n'),
    [children],
  );

  if (!inline && syntaxHighlighterLanguage) {
    const currentLanguage = syntaxHighlighterLanguage[1] as ProgrammingLanguage;

    return (
      <div className="nj-markdown-block-wrapper">
        <div className="nj-markdown-copy-block-wrapper">
          <h6>{currentLanguage}</h6>

          <div className="nj-markdown-copy-block-wrapper-actions">
            <CopyCodeButton content={sanitizedText} color="link" />

            {!isVisibleTaskViewModal && (
              <MoveToEditorTool
                disabled={isStreaming}
                componentType={ArtifactType.CODE}
                codeContent={sanitizedText}
                language={currentLanguage}
              />
            )}
          </div>
        </div>
        <hr className="divider" />

        <SyntaxHighlighter
          style={isDarkTheme ? atomOneDarkReasonable : atomOneLight}
          language={currentLanguage}
          PreTag="div"
          // attn: if "showLineNumbers" is true, "wrapLongLines" must be false. Issue: https://github.com/react-syntax-highlighter/react-syntax-highlighter/issues/376
          wrapLongLines={true}
          showLineNumbers={false}
          {...props}
        >
          {String(children).replace(/\n$/, '')}
        </SyntaxHighlighter>
      </div>
    );
  }

  return (
    <code className={className} {...props}>
      {children}
    </code>
  );
};
